import axios, { AxiosInstance } from 'axios';
import type { ChatMessage, ArticleSource, RagResponse, QuickReply } from '@/components/ChatWidget/types';

export type { ChatMessage, ArticleSource, RagResponse, QuickReply };

const API_BASE_URL = '/api';
const DEFAULT_TENANT_ID = '11111111-1111-1111-1111-111111111111';

export interface ChatSession {
  visitorId: string;
  sessionId?: string;
}

export interface ChatConversation {
  id: string;
  sessionId?: string;
  status: string;
  createdAt: string;
}

export interface LiveChatSession {
  id: string;
  visitorId: string;
  status: 'waiting' | 'active' | 'ended';
  assignedAgentId?: string;
  createdAt: string;
}

// Response from sendMessage endpoint (includes AI response)
export interface SendMessageResponse {
  visitorMessage: ChatMessage;
  aiMessage: ChatMessage | null;
}

// Configurable Chat Service Class
export class ChatServiceClient {
  private chatApi: AxiosInstance;
  private ragApi: AxiosInstance;
  private knowledgeApi: AxiosInstance;
  private storagePrefix: string;

  constructor(tenantId: string = DEFAULT_TENANT_ID) {
    this.storagePrefix = `chat_${tenantId}_`;

    const headers = {
      'Content-Type': 'application/json',
      'x-tenant-id': tenantId,
    };

    this.chatApi = axios.create({
      baseURL: `${API_BASE_URL}/chat`,
      headers,
      // Cap how long we wait for an AI response. Without this the widget could
      // hang forever with the typing indicator stuck if the AI/RAG call stopped
      // responding (bug #9682); a timeout surfaces an error so the UI can show a
      // fallback. Slightly above the gateway's ~30s AI timeout.
      timeout: 45000,
    });

    this.ragApi = axios.create({
      baseURL: `${API_BASE_URL}/rag`,
      headers,
    });

    this.knowledgeApi = axios.create({
      baseURL: API_BASE_URL,
      headers,
    });
  }

  // Storage helpers with tenant isolation
  private getStoredVisitorId(): string | null {
    try {
      return localStorage.getItem(`${this.storagePrefix}visitor_id`);
    } catch {
      return null;
    }
  }

  private storeVisitorId(visitorId: string): void {
    try {
      localStorage.setItem(`${this.storagePrefix}visitor_id`, visitorId);
    } catch {
      // Ignore storage errors
    }
  }

  private storeConversationId(conversationId: string): void {
    try {
      localStorage.setItem(`${this.storagePrefix}conversation_id`, conversationId);
    } catch {
      // Ignore storage errors
    }
  }

  async initSession(visitorInfo?: { name?: string; email?: string }): Promise<ChatSession> {
    const existingVisitorId = this.getStoredVisitorId();

    try {
      const response = await this.chatApi.post<ChatSession>('/session', {
        visitorInfo,
        existingVisitorId,
      });

      if (response.data.visitorId) {
        this.storeVisitorId(response.data.visitorId);
      }

      return response.data;
    } catch (error) {
      // Fallback: generate a temporary visitor ID
      const fallbackVisitorId = existingVisitorId || `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.storeVisitorId(fallbackVisitorId);
      return { visitorId: fallbackVisitorId };
    }
  }

  // Guest pre-chat email verification: send a code, then verify it before the
  // chat starts. The code is emailed by the BFF and stored server-side (Redis).
  async sendPreChatOtp(
    email: string,
    name?: string,
  ): Promise<{ maskedEmail: string; expiresInSeconds: number }> {
    const response = await this.chatApi.post<{ maskedEmail: string; expiresInSeconds: number }>(
      '/prechat/send-otp',
      { email, name },
    );
    return response.data;
  }

  async verifyPreChatOtp(
    email: string,
    code: string,
  ): Promise<{ ok: true } | { ok: false; code?: string; message?: string }> {
    try {
      await this.chatApi.post('/prechat/verify-otp', { email, code });
      return { ok: true };
    } catch (error) {
      const data = (error as { response?: { data?: { code?: string; message?: string } } })?.response?.data;
      return { ok: false, code: data?.code, message: data?.message };
    }
  }

  async startConversation(
    visitorId: string,
    preChatData?: Record<string, string>
  ): Promise<ChatConversation> {
    try {
      const response = await this.chatApi.post<ChatConversation>(
        '/conversations',
        { preChatData },
        {
          headers: {
            'x-visitor-id': visitorId,
          },
        }
      );

      if (response.data.id) {
        this.storeConversationId(response.data.id);
      }

      return response.data;
    } catch (error) {
      // Fallback: create a local conversation ID
      const conversationId = `conv_${Date.now()}`;
      this.storeConversationId(conversationId);
      return {
        id: conversationId,
        status: 'active',
        createdAt: new Date().toISOString(),
      };
    }
  }

  async sendMessage(
    conversationId: string,
    visitorId: string,
    content: string,
    attachmentIds?: string[]
  ): Promise<SendMessageResponse> {
    console.log(`[ChatService] sendMessage: conversation=${conversationId}, visitor=${visitorId}`);
    try {
      const response = await this.chatApi.post<SendMessageResponse>(
        `/conversations/${conversationId}/messages`,
        { content, attachmentIds },
        {
          headers: {
            'x-visitor-id': visitorId,
          },
          // Increase timeout for AI response (can take up to 30 seconds)
          timeout: 60000,
        }
      );
      console.log(`[ChatService] sendMessage success:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error(`[ChatService] sendMessage error:`, error.response?.data || error.message);
      // Re-throw to let the caller handle the error
      throw error;
    }
  }

  async getMessages(conversationId: string, visitorId: string): Promise<ChatMessage[]> {
    try {
      const response = await this.chatApi.get<ChatMessage[]>(
        `/conversations/${conversationId}/messages`,
        {
          headers: {
            'x-visitor-id': visitorId,
          },
        }
      );
      return response.data;
    } catch (error) {
      return [];
    }
  }

  // Knowledge Base Integration - RAG Query
  async queryKnowledgeBase(query: string, conversationId?: string): Promise<RagResponse> {
    try {
      const response = await this.ragApi.post<RagResponse>('/query', {
        query,
        maxSources: 3,
        includeCitations: true,
        conversationContext: conversationId,
      });

      // Check if the response indicates a handoff is needed
      const metadata = response.data.metadata as any;
      const needsHandoff = metadata?.needsHandoff === true;

      // Add quick replies based on confidence and context
      let quickReplies = this.generateQuickReplies(response.data, query);

      // If handoff is needed, ensure we have a "Talk to Agent" button and mark the response
      if (needsHandoff) {
        quickReplies = [
          { id: 'confirm-handoff', label: 'Yes, connect me', value: 'Please connect me to an agent now' },
          { id: 'cancel-handoff', label: 'No, I have another question', value: 'Actually, I have another question' },
        ];
      }

      return {
        ...response.data,
        quickReplies,
        needsHandoff,
      };
    } catch (error) {
      console.error('RAG query failed:', error);
      return {
        answer: "I couldn't find specific information in our knowledge base for your question. Would you like to speak with a support agent?",
        sources: [],
        quickReplies: [
          { id: 'handoff', label: 'Talk to Agent', value: 'I want to speak with a support agent' },
          { id: 'rephrase', label: 'Try Again', value: 'Let me rephrase my question' },
        ],
      };
    }
  }

  private generateQuickReplies(response: RagResponse, _query: string): QuickReply[] {
    const replies: QuickReply[] = [];

    // If low confidence, suggest speaking to an agent
    if (response.confidence && response.confidence < 0.5) {
      replies.push({
        id: 'handoff',
        label: 'Talk to Agent',
        value: 'I want to speak with a support agent',
      });
    }

    // Add "Learn more" if sources available
    if (response.sources && response.sources.length > 0) {
      replies.push({
        id: 'more-info',
        label: 'Tell me more',
        value: `Tell me more about ${response.sources[0].title}`,
      });
    }

    // Add common follow-ups
    replies.push({
      id: 'helpful',
      label: 'This helped!',
      value: 'Thank you, this was helpful!',
    });

    return replies.slice(0, 3);
  }

  // Search knowledge base without AI response
  async searchKnowledge(query: string, limit: number = 5): Promise<ArticleSource[]> {
    try {
      const response = await this.knowledgeApi.get(`/search?query=${encodeURIComponent(query)}&limit=${limit}`);
      const results = response.data.results || [];
      return results
        .filter((r: any) => r.type === 'article')
        .map((r: any) => ({
          id: r.id,
          title: r.title,
          slug: r.slug,
          excerpt: r.excerpt,
        }));
    } catch (error) {
      console.error('Knowledge search failed:', error);
      return [];
    }
  }

  // Get suggested questions
  async getSuggestions(query: string): Promise<string[]> {
    try {
      const response = await this.ragApi.get(`/suggestions?query=${encodeURIComponent(query)}&limit=5`);
      return response.data || [];
    } catch (error) {
      return [];
    }
  }

  // Check if query can be answered from knowledge base
  async canAnswer(query: string): Promise<{ canAnswer: boolean; confidence: number }> {
    try {
      const response = await this.ragApi.get(`/can-answer?query=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      return { canAnswer: false, confidence: 0 };
    }
  }

  // Submit feedback for a message
  async submitFeedback(queryId: string, feedback: 'positive' | 'negative', comment?: string): Promise<void> {
    try {
      await this.ragApi.post('/feedback', {
        queryId,
        helpful: feedback === 'positive',
        comment,
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  }

  async getConfig(): Promise<{ available: boolean; agentName: string; offlineMessage: string }> {
    try {
      const response = await this.chatApi.get('/config');
      return response.data;
    } catch (error) {
      return {
        available: true,
        agentName: 'AI Assistant',
        offlineMessage: 'We are currently offline. Please leave a message.',
      };
    }
  }

  // Create a live chat session for agent handoff
  async createLiveChatSession(
    visitorId: string,
    metadata?: Record<string, string>
  ): Promise<LiveChatSession | null> {
    try {
      const response = await this.chatApi.post<LiveChatSession>(
        '/sessions',
        {
          visitorId,
          channel: 'web_chat',
          metadata: {
            ...metadata,
            source: 'chat_widget',
            handoff: true,
          },
        },
        {
          headers: {
            'x-visitor-id': visitorId,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create live chat session:', error);
      return null;
    }
  }

  // Send a message to an active live chat session
  async sendSessionMessage(
    sessionId: string,
    visitorId: string,
    content: string
  ): Promise<ChatMessage> {
    try {
      const response = await this.chatApi.post<ChatMessage>(
        `/sessions/${sessionId}/visitor-messages`,
        {
          content,
          type: 'text',
        },
        {
          headers: {
            'x-visitor-id': visitorId,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to send session message:', error);
      // Return local message on error
      return {
        id: `msg_${Date.now()}`,
        content,
        senderType: 'visitor' as const,
        createdAt: new Date().toISOString(),
      };
    }
  }

  // Get message history for a session
  async getSessionMessages(sessionId: string): Promise<ChatMessage[]> {
    const visitorId = this.getStoredVisitorId();
    try {
      const response = await this.chatApi.get<ChatMessage[]>(
        `/sessions/${sessionId}/visitor-messages`,
        {
          headers: visitorId ? { 'x-visitor-id': visitorId } : {},
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get session messages:', error);
      return [];
    }
  }

  // Explicitly ask for a human agent. AI conversations start in 'bot' status and
  // are NOT in the agent queue; this escalates the conversation into the waiting
  // queue only when the customer requests it (#9690).
  async requestHandoff(sessionId: string, reason?: string): Promise<{ id: string; status: string }> {
    const visitorId = this.getStoredVisitorId();
    try {
      const response = await this.chatApi.post<{ id: string; status: string }>(
        `/sessions/${sessionId}/request-handoff`,
        { reason },
        {
          headers: visitorId ? { 'x-visitor-id': visitorId } : {},
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to request handoff:', error);
      // Best-effort: treat as waiting so the widget can still switch to live mode.
      return { id: sessionId, status: 'waiting' };
    }
  }

  // End a chat session (triggers ticket creation)
  async endSession(sessionId: string, reason?: string): Promise<{ ticketCreated: boolean; ticketNumber?: string }> {
    const visitorId = this.getStoredVisitorId();
    try {
      const response = await this.chatApi.post<{
        ticketCreated: boolean;
        ticketNumber?: string;
        ticketId?: string;
      }>(
        `/sessions/${sessionId}/visitor-end`,
        { reason },
        {
          headers: visitorId ? { 'x-visitor-id': visitorId } : {},
        }
      );
      return {
        ticketCreated: response.data.ticketCreated,
        ticketNumber: response.data.ticketNumber,
      };
    } catch (error) {
      // Rethrown: answering "no ticket" made the chat say it had ended while
      // the session stayed open on the server.
      console.error('Failed to end session:', error);
      throw error;
    }
  }
}

// Default instance for backward compatibility
export const chatService = new ChatServiceClient();

// Factory function to create tenant-specific clients
export function createChatService(tenantId: string): ChatServiceClient {
  return new ChatServiceClient(tenantId);
}
